'use client';

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  SquaresFour,
  Funnel,
  Buildings,
  Users,
  RocketLaunch,
  Gear,
  MagnifyingGlass,
  FunnelSimple,
  Sparkle,
  SlidersHorizontal,
  SpinnerGap,
  CheckCircle,
  LinkedinLogo,
  PaperPlaneTilt,
  Database,
  Globe,
  UsersFour,
  ArrowsClockwise,
  Cursor,
} from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type DemoStep = 'q_and_a' | 'pipeline_running' | 'companies' | 'people' | 'outreach';

const STEP_TO_SIDEBAR: Record<DemoStep, string> = {
  q_and_a: 'Agent',
  pipeline_running: 'Pipeline',
  companies: 'Companies',
  people: 'People',
  outreach: 'Automations',
};

const AUTO_ADVANCE_MS = 10_000;

/* ------------------------------------------------------------------ */
/*  Demo data — real companies from our DB                             */
/* ------------------------------------------------------------------ */

const DEMO_COMPANIES = [
  { initials: 'n8', color: '#ea4b71', name: 'n8n', domain: 'n8n.io', industry: 'Software', size: 125, aiScore: 9, badges: ['Target Size', 'Tech Match'], logo: 'https://media.licdn.com/dms/image/v2/C4E0BAQGjfyqCAwh66A/company-logo_200_200/company-logo_200_200/0/1655126280523/n8n_logo?e=2147483647&v=beta&t=pHNgNEYmrNAGSjV0A1WnGYgZIo8TpksWNJcrIBijMVg' },
  { initials: 'UL', color: '#6366f1', name: 'Unlimit', domain: 'unlimit.com', industry: 'Financial Services', size: 350, aiScore: 9, badges: ['Target Size'], logo: 'https://media.licdn.com/dms/image/v2/D4E0BAQHlzNnQI6YDfw/company-logo_200_200/B4EZeb1ReUHwAM-/0/1750666124260/unlimit_com_logo?e=2147483647&v=beta&t=81cbbegrtED5_riVrnnJPx3K-dzSjFCJY8BSairdJAs' },
  { initials: 'AL', color: '#f59e0b', name: 'Alpaca', domain: 'alpaca.markets', industry: 'Financial Services', size: 350, aiScore: 9, badges: ['Target Size'], logo: 'https://media.licdn.com/dms/image/v2/C4E0BAQFV91nqVMKLlQ/company-logo_200_200/company-logo_200_200/0/1659022454906/alpacadb_inc__logo?e=2147483647&v=beta&t=oUyNcDpwWkFL4t0Uwfjq4bhbxeFACRaIv80N_zcPqjM' },
  { initials: 'RF', color: '#10b981', name: 'refurbed', domain: 'refurbed.com', industry: 'Retail', size: 350, aiScore: 9, badges: ['Target Size', 'Tech Match'], logo: 'https://media.licdn.com/dms/image/v2/D4D0BAQEIpr5HnNPArA/company-logo_200_200/company-logo_200_200/0/1725348644476/refurbed_logo?e=2147483647&v=beta&t=AtfANIu4gfXTsnv59tbM8PJEnJIaLv8FuOSX_riN5Mk' },
  { initials: 'TS', color: '#3b82f6', name: 'Topsort', domain: 'topsort.com', industry: 'Technology', size: 125, aiScore: 9, badges: ['Target Size', 'Tech Match'], logo: 'https://media.licdn.com/dms/image/v2/D4E0BAQHRvac9Q6igzA/company-logo_200_200/B4EZZoZvJfHkAI-/0/1745508299722/topsort_logo?e=2147483647&v=beta&t=XdZAjFBZBx0VmjoBWyQSZlVjCjpPNA8JYaW5ie_q4hk' },
  { initials: 'SR', color: '#0ea5e9', name: 'SORACOM', domain: 'soracom.io', industry: 'Telecom', size: 125, aiScore: 9, badges: ['Target Size'], logo: 'https://media.licdn.com/dms/image/v2/C4D0BAQEDa_Yu2KH_MQ/company-logo_200_200/company-logo_200_200/0/1648573769637/soracom_inc_logo?e=2147483647&v=beta&t=wFMRbeLROBkAe8LvfD3kBIyJ3_VDUVepUmEz154Ps-Y' },
];

const DEMO_PEOPLE = [
  { initials: 'JO', color: '#2563eb', name: 'Jan Oberhauser', title: 'CEO & Founder', company: 'n8n', icpScore: 9, status: 'Ready', email: 'j.ob***@n8n.io' },
  { initials: 'AK', color: '#059669', name: 'Anna Kuzmina', title: 'VP Engineering', company: 'Unlimit', icpScore: 9, status: 'Ready', email: 'a.ku***@unlimit.com' },
  { initials: 'YY', color: '#7c3aed', name: 'Yoshi Yago', title: 'Co-founder', company: 'Alpaca', icpScore: 8, status: 'Ready', email: 'y.ya***@alpaca.markets' },
  { initials: 'PW', color: '#dc2626', name: 'Peter W.', title: 'Co-founder', company: 'refurbed', icpScore: 9, status: 'Ready', email: 'p.wi***@refurbed.com' },
  { initials: 'RY', color: '#ea580c', name: 'Regina Ye', title: 'Co-founder', company: 'Topsort', icpScore: 8, status: 'Enriching', email: 'r.ye***@topsort.com' },
];

const QA_INDUSTRIES = ['Technology', 'Financial Services', 'Retail & E-commerce', 'Healthcare'];

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

const ScoreBadge = ({ score }: { score: number | null }) => {
  if (score === null) return <span className="text-[11px] text-gray-400">--</span>;
  const bg = score >= 8 ? 'bg-emerald-50 text-emerald-700' : score >= 5 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600';
  return <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${bg}`}>{score}/10</span>;
};

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    Ready: 'bg-emerald-50 text-emerald-700',
    Enriching: 'bg-amber-50 text-amber-700',
    New: 'bg-blue-50 text-blue-600',
  };
  return <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ${styles[status] ?? 'bg-gray-100 text-gray-500'}`}>{status}</span>;
};

const TypeBadges = ({ badges }: { badges: string[] }) => {
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-0.5">
      {badges.map((b) => (
        <span key={b} className="rounded px-1 py-[1px] text-[10px] font-medium bg-teal-50 text-teal-700 whitespace-nowrap">{b}</span>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Sidebar                                                            */
/* ------------------------------------------------------------------ */

const NAV_ITEMS = [
  { icon: SquaresFour, label: 'Agent' },
  { icon: Funnel, label: 'Pipeline' },
  { icon: Buildings, label: 'Companies' },
  { icon: Users, label: 'People' },
  { icon: RocketLaunch, label: 'Automations' },
];

const NAV_TO_STEP: Record<string, DemoStep> = {
  Agent: 'q_and_a',
  Pipeline: 'pipeline_running',
  Companies: 'companies',
  People: 'people',
  Automations: 'outreach',
};

const MockupSidebar = ({
  step,
  onNavigate,
  showPeopleBadge,
}: {
  step: DemoStep;
  onNavigate: (step: DemoStep) => void;
  showPeopleBadge: boolean;
}) => {
  const activeLabel = STEP_TO_SIDEBAR[step];

  return (
    <div className="hidden lg:flex flex-col w-[48px] bg-transparent py-3 items-center shrink-0">
      <div className="flex-1 flex flex-col gap-1 items-center mt-1">
        {NAV_ITEMS.map((item) => {
          const isActive = item.label === activeLabel;
          const Icon = item.icon;
          const targetStep = NAV_TO_STEP[item.label];
          const shouldPulse = item.label === 'People' && step === 'companies';

          return (
            <button
              key={item.label}
              type="button"
              onClick={() => onNavigate(targetStep)}
              className={`relative flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-150 cursor-pointer ${
                isActive ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-400 hover:text-teal-700 hover:bg-gray-100'
              } ${shouldPulse ? 'demo-hint-pulse' : ''}`}
            >
              <Icon size={15} weight={isActive ? 'fill' : 'regular'} />
              {item.label === 'People' && showPeopleBadge && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[7px] font-bold text-white ring-2 ring-white">5</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex h-8 w-8 items-center justify-center text-gray-400">
        <Gear size={16} />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Top bar                                                            */
/* ------------------------------------------------------------------ */

const MockupTopBar = () => (
  <div className="flex items-center gap-2 border-b border-gray-200/60 px-3 py-2">
    <div className="flex flex-1 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50/60 px-2 py-1">
      <MagnifyingGlass size={11} className="text-gray-400 shrink-0" />
      <span className="text-[11px] text-gray-400 truncate">Search by name, industry, or tech...</span>
    </div>
    <div className="hidden sm:flex items-center gap-1.5">
      <button className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-500">
        <FunnelSimple size={10} /> Filters
      </button>
      <button className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-teal-600 font-medium">
        <Sparkle size={10} /> ICP Ready
      </button>
      <button className="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-500">
        <SlidersHorizontal size={10} /> Columns
      </button>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Step 1: Q&A View                                                   */
/* ------------------------------------------------------------------ */

const QandAView = ({ onSelect }: { onSelect: () => void }) => {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = React.useState(false);

  const toggle = (industry: string) => {
    if (submitted) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(industry)) next.delete(industry); else next.add(industry);
      return next;
    });
  };

  const handleContinue = () => {
    if (submitted) return;
    setSubmitted(true);
    setTimeout(() => onSelect(), 500);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 px-3 py-1.5">
      {/* Q1: AI question - left aligned */}
      <div className="flex mb-0.5">
        <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1 max-w-[80%]">
          <div className="text-[9px] font-semibold text-teal-600 flex items-center gap-1"><Sparkle size={8} weight="fill" />AI Planner</div>
          <div className="text-[10px] text-gray-700">What problem does your product solve?</div>
        </div>
      </div>
      {/* A1: right-aligned answer */}
      <div className="flex justify-end mb-1">
        <div className="rounded-md bg-teal-50 border border-teal-100 px-2 py-1 text-[10px] text-gray-700 max-w-[80%]">We help companies find leads and contact them</div>
      </div>
      {/* Q2 - left aligned */}
      <div className="flex mb-1">
        <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1 max-w-[80%]">
          <div className="text-[9px] font-semibold text-teal-600 flex items-center gap-1"><Sparkle size={8} weight="fill" />AI Planner</div>
          <div className="text-[10px] text-gray-700">What industries are your ideal customers in?</div>
        </div>
      </div>

      <div className="h-1" />

      {/* Checkboxes */}
      <div className="space-y-[3px] flex-1 min-h-0">
        {QA_INDUSTRIES.map((industry) => {
          const isChecked = selected.has(industry);
          return (
            <button
              key={industry}
              type="button"
              onClick={() => toggle(industry)}
              className={`w-full flex items-center gap-2 rounded-md border px-2 py-[4px] text-[11px] transition-all duration-150 text-left ${
                isChecked
                  ? 'border-teal-400 bg-teal-50/50 text-gray-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <div className={`h-3 w-3 rounded-[3px] border flex items-center justify-center shrink-0 ${isChecked ? 'border-teal-500 bg-teal-500' : 'border-gray-300'}`}>
                {isChecked && <CheckCircle size={8} weight="bold" className="text-white" />}
              </div>
              {industry}
            </button>
          );
        })}
      </div>

      {/* Continue */}
      <button
        onClick={handleContinue}
        className={`w-full rounded-lg py-[5px] mt-1 text-[11px] font-semibold flex items-center justify-center gap-1 transition-all ${
          selected.size > 0
            ? 'bg-teal-600 text-white hover:bg-teal-700 cursor-pointer'
            : 'bg-gray-100 text-gray-400'
        }`}
      >
        Continue{selected.size > 0 ? ` (${selected.size})` : ''}
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Step 2: Pipeline Running View                                      */
/* ------------------------------------------------------------------ */

interface PipelineStepDef {
  icon: typeof Globe;
  label: string;
  subtitle: string;
  delay: number;
}

const PIPELINE_STEPS: PipelineStepDef[] = [
  { icon: RocketLaunch, label: 'Launch pipeline', subtitle: 'Initializing search', delay: 500 },
  { icon: Globe, label: 'Discover companies', subtitle: 'Searching Google SERP', delay: 800 },
  { icon: Database, label: 'Score & filter', subtitle: 'AI scoring 115 companies', delay: 800 },
  { icon: UsersFour, label: 'Find people', subtitle: 'Finding decision-makers', delay: 700 },
  { icon: ArrowsClockwise, label: 'Wrap-up', subtitle: 'Preparing results', delay: 400 },
];

const PipelineRunningView = ({ onComplete }: { onComplete: () => void }) => {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [doneSet, setDoneSet] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < PIPELINE_STEPS.length; i++) {
        if (cancelled) return;
        setActiveIdx(i);
        await new Promise((r) => setTimeout(r, PIPELINE_STEPS[i].delay));
        if (cancelled) return;
        setDoneSet((prev) => new Set(prev).add(i));
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!cancelled) {
        await new Promise((r) => setTimeout(r, 400));
        onComplete();
      }
    };
    run();
    return () => { cancelled = true; };
  }, [onComplete]);

  return (
    <div className="flex flex-col flex-1 min-h-0 px-3 py-3 overflow-hidden">
      <div className="mb-2 text-[11px] font-medium text-teal-600 uppercase tracking-wider px-1">Lead Generation Pipeline</div>
      <div className="flex-1 min-h-0 space-y-1.5">
        {PIPELINE_STEPS.map((s, i) => {
          const isDone = doneSet.has(i);
          const isActive = i === activeIdx && !isDone;
          const isPending = i > activeIdx;
          const Icon = s.icon;

          return (
            <motion.div
              key={s.label}
              initial={i > 0 ? { opacity: 0.5 } : false}
              animate={{ opacity: 1 }}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 transition-all ${
                isActive
                  ? 'border-teal-200 bg-teal-50/60'
                  : isDone
                    ? 'border-gray-100 bg-white'
                    : 'border-gray-100 bg-gray-50/30'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon size={13} weight={isDone || isActive ? 'fill' : 'regular'} className={isActive ? 'text-teal-600' : isDone ? 'text-teal-500' : 'text-gray-300'} />
                <div className="min-w-0">
                  <div className={`text-[10px] font-semibold leading-tight ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>{s.label}</div>
                  <div className={`text-[10px] leading-tight ${isActive ? 'text-teal-600' : 'text-gray-400'}`}>{s.subtitle}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                {isDone && (
                  <>
                    <span className="text-[10px] text-gray-400">Done</span>
                    <CheckCircle size={12} weight="fill" className="text-teal-500" />
                  </>
                )}
                {isActive && (
                  <>
                    <span className="text-[10px] text-teal-600 font-medium">Running</span>
                    <SpinnerGap size={12} className="text-teal-500 animate-spin" />
                  </>
                )}
                {isPending && <span className="text-[10px] text-gray-300">Pending</span>}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Step 3: Companies table                                            */
/* ------------------------------------------------------------------ */

const CompaniesView = ({ onFindPeople }: { onFindPeople: () => void }) => (
  <div className="flex flex-col flex-1 min-h-0">
    <MockupTopBar />
    <div className="flex-1 min-h-0 overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/40">
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1">Company</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1 hidden sm:table-cell">Industry</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1 hidden sm:table-cell">Size</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">Score</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">Type</th>
          </tr>
        </thead>
        <tbody>
          {DEMO_COMPANIES.map((c) => (
            <tr key={c.name} className="border-b border-gray-100/70">
              <td className="px-3 py-[5px]">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={c.logo} alt="" className="h-6 w-6 object-cover" onError={(e) => { (e.target as HTMLImageElement).outerHTML = `<span class="text-[10px] font-bold text-gray-500">${c.initials}</span>`; }} />
                  </div>
                  <div>
                    <div className="text-[10px] font-medium text-gray-900 leading-tight whitespace-nowrap">{c.name}</div>
                    <div className="text-[10px] text-teal-600 leading-tight whitespace-nowrap">{c.domain}</div>
                  </div>
                </div>
              </td>
              <td className="text-[11px] text-gray-600 px-2 py-[5px] hidden sm:table-cell whitespace-nowrap">{c.industry}</td>
              <td className="text-[11px] text-gray-600 px-2 py-[5px] hidden sm:table-cell">{c.size}</td>
              <td className="px-2 py-[5px]"><ScoreBadge score={c.aiScore} /></td>
              <td className="px-2 py-[5px]"><TypeBadges badges={c.badges} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50/30">
      <button
        onClick={onFindPeople}
        className="flex items-center gap-1.5 rounded-lg border border-teal-400 bg-teal-600 px-3 py-1.5 text-[11px] text-white font-semibold cursor-pointer demo-hint-pulse hover:bg-teal-700 transition-colors shadow-sm"
      >
        <Users size={12} weight="fill" /> Find People
      </button>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-gray-400">115 companies</span>
        <span className="flex h-4 w-4 items-center justify-center rounded text-[10px] font-semibold bg-teal-600 text-white">1</span>
        <span className="flex h-4 w-4 items-center justify-center rounded text-[10px] text-gray-400">2</span>
        <span className="flex h-4 w-4 items-center justify-center rounded text-[10px] text-gray-400">3</span>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Step 4: People table                                               */
/* ------------------------------------------------------------------ */

const PeopleViewWithOutreach = ({ onOutreach }: { onOutreach: () => void }) => (
  <div className="flex flex-col flex-1 min-h-0">
    <MockupTopBar />
    <div className="flex-1 min-h-0 overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/40">
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1">Name</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1 hidden sm:table-cell">Title</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1 hidden sm:table-cell">Company</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">Email</th>
            <th className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">ICP</th>
          </tr>
        </thead>
        <tbody>
          {DEMO_PEOPLE.map((p) => (
            <tr key={p.name} className="border-b border-gray-100/70">
              <td className="px-3 py-[5px]">
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <div className="h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0" style={{ backgroundColor: p.color }}>{p.initials}</div>
                  <span className="text-[10px] font-medium text-gray-900">{p.name}</span>
                </div>
              </td>
              <td className="text-[10px] text-gray-600 px-2 py-[5px] hidden sm:table-cell whitespace-nowrap">{p.title}</td>
              <td className="text-[10px] text-gray-600 px-2 py-[5px] hidden sm:table-cell whitespace-nowrap">{p.company}</td>
              <td className="text-[10px] text-gray-500 px-2 py-[5px] whitespace-nowrap">{p.email}</td>
              <td className="px-2 py-[5px]"><ScoreBadge score={p.icpScore} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50/30">
      <button
        onClick={onOutreach}
        className="flex items-center gap-1.5 rounded-lg border border-teal-400 bg-teal-600 px-2.5 py-1 text-[11px] text-white font-semibold demo-hint-pulse cursor-pointer hover:bg-teal-700 transition-colors shadow-sm"
      >
        <PaperPlaneTilt size={11} weight="fill" /> Start Outreach
      </button>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-gray-400">42 contacts</span>
        <span className="flex h-4 w-4 items-center justify-center rounded text-[10px] font-semibold bg-teal-600 text-white">1</span>
        <span className="flex h-4 w-4 items-center justify-center rounded text-[10px] text-gray-400">2</span>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Step 5: LinkedIn Browser Automation View                           */
/* ------------------------------------------------------------------ */

const OUTREACH_CONTACTS = [
  { initials: 'JO', color: '#2563eb', name: 'Jan Oberhauser', title: 'CEO & Founder', company: 'n8n', location: 'Berlin, Germany', slug: 'jan-oberhauser', msg: 'Hi Jan, I saw n8n is growing fast...' },
  { initials: 'AK', color: '#059669', name: 'Anna Kuzmina', title: 'VP Engineering', company: 'Unlimit', location: 'London, UK', slug: 'anna-kuzmina', msg: 'Hi Anna, noticed Unlimit is scaling...' },
];

const OutreachView = ({ onComplete }: { onComplete: () => void }) => {
  const [contactIdx, setContactIdx] = React.useState(0);
  const [phase, setPhase] = React.useState<'profile' | 'modal' | 'sent'>('profile');

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // First contact: show profile, then modal, then sent
      await new Promise((r) => setTimeout(r, 1200));
      if (cancelled) return;
      setPhase('modal');
      await new Promise((r) => setTimeout(r, 2000));
      if (cancelled) return;
      setPhase('sent');
      await new Promise((r) => setTimeout(r, 1000));
      if (cancelled) return;

      // Second contact
      setContactIdx(1);
      setPhase('profile');
      await new Promise((r) => setTimeout(r, 1000));
      if (cancelled) return;
      setPhase('modal');
      await new Promise((r) => setTimeout(r, 1500));
      if (cancelled) return;
      setPhase('sent');
      await new Promise((r) => setTimeout(r, 1500));
      if (!cancelled) onComplete();
    };
    run();
    return () => { cancelled = true; };
  }, [onComplete]);

  const contact = OUTREACH_CONTACTS[contactIdx];

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Mini browser chrome */}
      <div className="flex items-center gap-2 border-b border-gray-200/60 px-3 py-1.5 bg-gray-50/60">
        <div className="flex items-center gap-1">
          <div className="h-[6px] w-[6px] rounded-full bg-gray-300" />
          <div className="h-[6px] w-[6px] rounded-full bg-gray-300" />
          <div className="h-[6px] w-[6px] rounded-full bg-gray-300" />
        </div>
        <div className="flex-1 rounded-md bg-white border border-gray-200 px-2 py-0.5 flex items-center gap-1.5">
          <LinkedinLogo size={10} weight="fill" className="text-[#0A66C2]" />
          <span className="text-[10px] text-gray-500 truncate">linkedin.com/in/{contact.slug}</span>
        </div>
      </div>

      {/* LinkedIn profile page */}
      <div className="flex-1 min-h-0 bg-[#f4f2ee] overflow-hidden relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={contactIdx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            {/* Profile card */}
            <div className="mx-2 mt-2 bg-white rounded-lg border border-gray-200/80 overflow-hidden">
              {/* Cover banner */}
              <div className="h-12 bg-gradient-to-r from-blue-200/60 via-blue-100/40 to-teal-100/30 relative">
                <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(59,130,246,0.3), transparent 60%), radial-gradient(circle at 80% 30%, rgba(20,184,166,0.2), transparent 50%)' }} />
              </div>

              <div className="px-3 pb-2.5 relative">
                {/* Avatar overlapping banner */}
                <div className="h-12 w-12 rounded-full border-[3px] border-white flex items-center justify-center text-[12px] font-bold text-white shrink-0 -mt-7 relative z-10 shadow-sm" style={{ backgroundColor: contact.color }}>{contact.initials}</div>

                {/* Name & info */}
                <div className="mt-1.5">
                  <div className="text-[12px] font-bold text-gray-900 leading-tight">{contact.name}</div>
                  <div className="text-[11px] text-gray-600 mt-0.5">{contact.title} at {contact.company}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{contact.location}</div>
                  <div className="text-[10px] text-[#0A66C2] font-medium mt-0.5">500+ connections</div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="flex items-center gap-1">
                    <div className="rounded-full bg-[#0A66C2] px-2.5 py-[3px] text-[10px] font-semibold text-white flex items-center gap-1">
                      <Users size={9} weight="bold" /> Connect
                    </div>
                    {phase === 'profile' && (
                      <motion.div animate={{ x: [-12, 0], y: [-6, 0] }} transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}>
                        <Cursor size={12} weight="fill" className="text-gray-700" />
                      </motion.div>
                    )}
                  </div>
                  <div className="rounded-full border border-[#0A66C2] px-2.5 py-[3px] text-[10px] font-semibold text-[#0A66C2]">Message</div>
                  <div className="rounded-full border border-gray-300 px-2 py-[3px] text-[10px] text-gray-500">More</div>
                </div>
              </div>
            </div>

            {/* Connection request modal overlay */}
            <AnimatePresence>
              {(phase === 'modal' || phase === 'sent') && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/20 flex items-center justify-center px-4"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-lg border border-gray-200 shadow-xl w-full max-w-[260px] overflow-hidden"
                  >
                    {phase === 'modal' ? (
                      <>
                        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-gray-900">Connect with {contact.name.split(' ')[0]}</span>
                          <span className="text-[11px] text-gray-400">x</span>
                        </div>
                        <div className="px-3 py-2.5">
                          <div className="text-[10px] text-gray-500 mb-1.5">Add a note to your connection request:</div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 min-h-[32px]">
                            <motion.div className="text-[11px] text-gray-700 leading-relaxed" initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1.5, ease: 'linear' }} style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                              {contact.msg}
                            </motion.div>
                          </div>
                          <div className="flex justify-end mt-2">
                            <div className="rounded-full bg-[#0A66C2] px-3 py-1 text-[10px] font-semibold text-white">Send</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-4 flex flex-col items-center gap-2">
                        <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                        <div className="text-[10px] font-semibold text-gray-900">Request sent!</div>
                        <div className="text-[10px] text-gray-500">Connection request delivered to {contact.name.split(' ')[0]}</div>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>

        {/* Status bar at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-gray-200/60 px-3 py-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
            {phase !== 'sent' ? <SpinnerGap size={9} className="animate-spin text-teal-500" /> : <CheckCircle size={9} weight="fill" className="text-teal-500" />}
            <span>{phase === 'profile' ? 'Opening profile...' : phase === 'modal' ? 'Sending request...' : 'Sent'}</span>
          </div>
          <span className="text-[10px] text-gray-400 font-medium">{contactIdx + 1}/2 contacts</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component — state machine                                     */
/* ------------------------------------------------------------------ */

const STEP_ORDER: DemoStep[] = ['q_and_a', 'pipeline_running', 'companies', 'people', 'outreach'];

function nextStep(current: DemoStep): DemoStep {
  const idx = STEP_ORDER.indexOf(current);
  return STEP_ORDER[(idx + 1) % STEP_ORDER.length];
}

const AppMockup = () => {
  const [step, setStep] = React.useState<DemoStep>('q_and_a');

  // Auto-advance timer for interactive steps
  React.useEffect(() => {
    if (step === 'pipeline_running' || step === 'outreach') return;
    const t = setTimeout(() => {
      setStep(nextStep(step));
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [step]);

  const handleQASelect = React.useCallback(() => setStep('pipeline_running'), []);
  const handlePipelineComplete = React.useCallback(() => setStep('companies'), []);
  const goToPeople = React.useCallback(() => setStep('people'), []);
  const goToOutreach = React.useCallback(() => setStep('outreach'), []);
  const handleOutreachComplete = React.useCallback(() => setStep('q_and_a'), []);
  const handleNavigate = React.useCallback((s: DemoStep) => setStep(s), []);

  const showPeopleBadge = step === 'companies';

  const contentView = (
    <AnimatePresence mode="wait">
      <motion.div
        key={step}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="h-full flex flex-col min-w-0"
      >
        {step === 'q_and_a' && <QandAView onSelect={handleQASelect} />}
        {step === 'pipeline_running' && <PipelineRunningView onComplete={handlePipelineComplete} />}
        {step === 'companies' && <CompaniesView onFindPeople={goToPeople} />}
        {step === 'people' && <PeopleViewWithOutreach onOutreach={goToOutreach} />}
        {step === 'outreach' && <OutreachView onComplete={handleOutreachComplete} />}
      </motion.div>
    </AnimatePresence>
  );

  const bottomNav = (
    <div className="flex border-t border-gray-200/60 bg-white px-2 py-1.5">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const navStep = NAV_TO_STEP[item.label];
        const isActive = item.label === STEP_TO_SIDEBAR[step];
        return (
          <button
            key={item.label}
            type="button"
            onClick={() => handleNavigate(navStep)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-0.5 transition-colors ${isActive ? 'text-teal-600' : 'text-gray-400'}`}
          >
            <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
            <span className="text-[7px] font-medium">{item.label === 'Automations' ? 'Outreach' : item.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Desktop: macOS window */}
      <div className="hidden lg:block rounded-2xl border border-black/[0.08] bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.06)] overflow-hidden">
        <div className="flex items-center px-3 py-2 bg-gray-100/80 border-b border-gray-200/60">
          <div className="flex items-center gap-1.5">
            <div className="h-[10px] w-[10px] rounded-full bg-[#FF5F57]" />
            <div className="h-[10px] w-[10px] rounded-full bg-[#FEBC2E]" />
            <div className="h-[10px] w-[10px] rounded-full bg-[#28C840]" />
          </div>
        </div>
        <div className="flex h-[320px] bg-gray-100/50">
          <MockupSidebar step={step} onNavigate={handleNavigate} showPeopleBadge={showPeopleBadge} />
          <div className="flex flex-col min-w-0 flex-1 p-2">
            <div className="flex-1 min-h-0 rounded-xl bg-white border border-gray-200/60 overflow-hidden shadow-sm">
              {contentView}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: phone mockup */}
      <div className="lg:hidden mx-auto w-[280px] sm:w-[320px]">
        <div className="rounded-[2rem] border-[3px] border-gray-800 bg-white overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
          {/* Phone status bar */}
          <div className="flex items-center justify-center px-4 py-1 bg-gray-100/80">
            <div className="w-16 h-1 rounded-full bg-gray-300" />
          </div>

          {/* Content */}
          <div className="h-[480px] sm:h-[520px] flex flex-col bg-gray-50/50">
            <div className="flex-1 min-h-0 m-1.5 rounded-xl bg-white border border-gray-200/60 overflow-hidden shadow-sm">
              {contentView}
            </div>

            {/* Bottom tab bar */}
            {bottomNav}
          </div>
        </div>
      </div>
    </>
  );
};

export default AppMockup;
